import { useQuery } from "@apollo/client";
import { withErrorBoundary } from "@sentry/react";
import { keyBy, uniq } from "lodash";
import moment from "moment";
import {
  GetPeopleTableMetadataQueryQuery,
  GetPeopleTableMetadataQueryQueryVariables,
} from "types/graphql-schema";
import { BasicUser } from "types/topicflow";

import getPeopleTableMetadataQuery from "@apps/dashboard-new/graphql/get-people-table-metadata-query";
import useUiPreferenceCache from "@apps/use-ui-preference-cache/use-ui-preference-cache";
import { currentOrganizationVar, currentUserVar } from "@cache/cache";
import Error from "@components/error/error";
import Layout from "@components/layout/layout";
import { assertEdgesNonNull } from "@helpers/helpers";

import HomePeopleRow from "./people-row";

export const peopleTableGoalMax = 5;

const HomePeopleTableComponent = () => {
  const currentUser = currentUserVar();
  const organization = currentOrganizationVar();
  const { uiPreferenceCache } = useUiPreferenceCache();

  const managers = currentUser?.managers
    ? assertEdgesNonNull(currentUser.managers)
    : [];
  const reports = currentUser?.directReports
    ? assertEdgesNonNull(currentUser.directReports)
    : [];
  const peers = currentUser?.peers ? assertEdgesNonNull(currentUser.peers) : [];
  const favourites = currentUser?.favouritesGroups
    ? assertEdgesNonNull(currentUser.favouritesGroups)
    : [];
  const favouriteUsers = favourites.reduce(
    (memo, favouriteGroup) => {
      const users = assertEdgesNonNull(favouriteGroup.users).map((user) => ({
        ...user,
      }));
      return [...memo, ...users];
    },
    [] as (BasicUser & { __typename: "UserNode" })[],
  );

  const oneononeUserIds = uniq([
    ...reports.map((report) => report.id),
    ...managers.map((manager) => manager.id),
  ]);
  const meetingUserIds = uniq([
    ...peers.map((directTeamUser) => directTeamUser.id),
    ...favouriteUsers.map((favouriteUser) => favouriteUser.id),
  ]);
  const { data: metadataData, loading: metadataLoading } = useQuery<
    GetPeopleTableMetadataQueryQuery,
    GetPeopleTableMetadataQueryQueryVariables
  >(getPeopleTableMetadataQuery, {
    notifyOnNetworkStatusChange: true,
    skip: !uiPreferenceCache.dashboardConnectionsContainerExpanded,
    variables: {
      oneononeUserIds,
      meetingUserIds,
      hasOneononeUserIds: oneononeUserIds.length > 0,
      hasMeetingUserIds: meetingUserIds.length > 0,
      peopleTableGoalMax,
      currentUserId: currentUser.id,
      now: moment().startOf("hour").format(),
    },
  });
  const meetingUsersWithMetadata = metadataData?.meetingUsers
    ? assertEdgesNonNull(metadataData.meetingUsers)
    : [];
  const oneononeUsersWithMetadata = metadataData?.oneononeUsers
    ? assertEdgesNonNull(metadataData.oneononeUsers)
    : [];
  const allUsersWithMetadata = [
    ...oneononeUsersWithMetadata,
    ...meetingUsersWithMetadata,
  ].map((user) => ({
    ...user,
    meetings: user.meetings ? assertEdgesNonNull(user.meetings) : [],
  }));
  const metadatabyUserId = keyBy(allUsersWithMetadata, "id");

  const countClassName =
    "px-1.5 py-0.5 bg-gray-100 rounded-md font-semibold text-xs text-gray-900 flex items-center gap-0.5";

  // RENDER
  return (
    <>
      {reports.length > 0 && (
        <Layout.MainSubSection
          title={
            <div className="flex items-center gap-1.5">
              Reports<span className={countClassName}>{reports.length}</span>
            </div>
          }
        >
          <div className="rounded-md border p-4 flex flex-col gap-4">
            {reports.map((report) => (
              <HomePeopleRow
                selectedUser={currentUser}
                userMetadata={metadatabyUserId[report.id]}
                user={report}
                key={report.id}
                loadingArtifacts={metadataLoading}
              />
            ))}
          </div>
        </Layout.MainSubSection>
      )}
      {managers.length > 0 && (
        <Layout.MainSubSection
          title={
            <div className="flex items-center gap-1.5">
              Managers
              <span className={countClassName}>{managers.length}</span>
            </div>
          }
        >
          <div className="rounded-md border p-4">
            {managers.map((manager) => (
              <HomePeopleRow
                selectedUser={currentUser}
                userMetadata={metadatabyUserId[manager.id]}
                user={manager}
                key={manager.id}
                loadingArtifacts={metadataLoading}
              />
            ))}
          </div>
        </Layout.MainSubSection>
      )}
      {organization.featureFlags.homepageMyConnectionsPeers &&
        peers.length > 0 && (
          <Layout.MainSubSection
            title={
              <div className="flex items-center gap-1.5">
                Peers
                <span className={countClassName}>{peers.length}</span>
              </div>
            }
          >
            <div className="rounded-md border p-4 flex flex-col gap-4">
              {peers.map((directTeamUser) => (
                <HomePeopleRow
                  selectedUser={currentUser}
                  userMetadata={metadatabyUserId[directTeamUser.id]}
                  user={directTeamUser}
                  key={directTeamUser.id}
                  loadingArtifacts={metadataLoading}
                  hideOneOnOneCTA
                />
              ))}
            </div>
          </Layout.MainSubSection>
        )}
      {favouriteUsers.length > 0 && (
        <Layout.MainSubSection
          title={
            <div className="flex items-center gap-1.5">
              Followers
              <span className={countClassName}>{favouriteUsers.length}</span>
            </div>
          }
        >
          <div className="rounded-md border p-4 flex flex-col gap-4">
            {favouriteUsers.map((favouriteUser) => (
              <HomePeopleRow
                selectedUser={currentUser}
                userMetadata={metadatabyUserId[favouriteUser.id]}
                user={favouriteUser}
                key={favouriteUser.id}
                loadingArtifacts={metadataLoading}
                hideOneOnOneCTA
              />
            ))}
          </div>
        </Layout.MainSubSection>
      )}
    </>
  );
};

const HomePeople = withErrorBoundary(HomePeopleTableComponent, {
  fallback: <Error description={"The people table could not be rendered."} />,
});
export default HomePeople;
